import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const DROPDOWN_HEIGHT = 140;
const DROPDOWN_MARGIN = 2;

const SearchableDropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const updatePosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp =
      spaceBelow < DROPDOWN_HEIGHT + DROPDOWN_MARGIN &&
      spaceAbove > spaceBelow;
    setOpenUpward(shouldOpenUp);
    setCoords({
      left: rect.left,
      width: rect.width,
      top: shouldOpenUp
        ? rect.top - DROPDOWN_HEIGHT
        : rect.bottom + DROPDOWN_MARGIN,
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => updatePosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      const dropdown = dropdownRef.current;
      const input = inputRef.current;
      if (
        dropdown &&
        !dropdown.contains(e.target) &&
        input &&
        !input.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp =
      spaceBelow < DROPDOWN_HEIGHT + DROPDOWN_MARGIN &&
      spaceAbove > spaceBelow;
    setOpenUpward(shouldOpenUp);
    setCoords({
      left: rect.left,
      width: rect.width,
      top: shouldOpenUp
        ? rect.top - DROPDOWN_HEIGHT
        : rect.bottom + DROPDOWN_MARGIN,
    });
  }, [open]);

  const filteredOptions = options.filter(
    (opt) =>
      opt?.label &&
      opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOption = options.find((opt) => opt.value === value);

  const handleToggle = () => {
    if (!disabled) setOpen((prev) => !prev);
  };

  const dropdownUI =
    open &&
    coords &&
    createPortal(
      <div
        ref={dropdownRef}
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          width: coords.width,
          zIndex: 9999,
        }}
        className={`bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden`}
      >
        {/* Search */}
        <div className="p-2 border-b bg-white">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full px-3 py-1.5 text-sm border rounded-md outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Options */}
        <div className="max-h-[140px] overflow-y-auto">
          {/* Clear */}
          {value && (
            <div
              className="px-3 py-2 text-xs text-red-500 cursor-pointer border-b sticky top-0 bg-white hover:bg-red-50"
              onClick={() => {
                onChange("");
                setOpen(false);
                setSearch("");
              }}
            >
              ✕ Clear Selection
            </div>
          )}

          {filteredOptions.length ? (
            filteredOptions.map((opt) => (
              <div
                key={opt.value}
                className={`px-3 py-2 text-sm cursor-pointer border-b 
                ${
                  value === opt.value
                    ? "bg-indigo-50 text-indigo-600 font-semibold"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-xs text-gray-400">
              No matches found
            </div>
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div className="relative w-full">
        {label && (
          <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">
            {label}
          </label>
        )}

        <div
          ref={inputRef}
          onClick={handleToggle}
          className={`w-full flex justify-between items-center px-3 py-2 text-sm border rounded-md cursor-pointer transition
          ${
            disabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white"
          }
          ${
            open
              ? "border-indigo-500 ring-2 ring-indigo-200"
              : "border-gray-300"
          }`}
        >
          <span
            className={`truncate flex-1 ${
              selectedOption ? "text-indigo-600" : "text-gray-400"
            }`}
          >
            {selectedOption
              ? selectedOption.label
              : placeholder}
          </span>

          <span
            className={`ml-2 text-xs transition-transform ${
              open ? "rotate-180" : ""
            }`}
          >
            ▼
          </span>
        </div>
      </div>

      {dropdownUI}
    </>
  );
};

export default SearchableDropdown;